import { resumePurchaseDraftMutation, savePurchaseDraftMutation } from './purchase-draft-mutation';
import { createInvoicePaymentCheckout, resumeInvoicePaymentCheckout } from './invoice-payment-checkout';
import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import { orContainsAnyColumn } from './postgrest-search';
import { sortByLineNumber } from './document-line-order';
import {
  chunkIds,
  compareDocumentsNewestFirst,
  completeCappedRows,
  fetchAllRows,
  withStableOrder,
} from './fetch-all-rows';
import type {
  IPurchaseRepository,
  PurchaseDocument,
  PurchaseDocType,
  PurchaseDocumentLine,
  PurchaseDocumentFilters,
  PurchaseDocumentSummary,
  DocumentWindowSignature,
  CreatePurchaseDocumentInput,
  CreatePurchaseLineInput,
  UpdatePurchaseDocumentInput,
  DocumentBalanceRow,
  ConflictingInvoiceSummary,
} from '../../../interfaces/src';
import {
  ParentAlreadyInvoicedError,
  ConcurrentEditError,
  PurchaseNumberTakenError,
} from '../../../interfaces/src';
import {
  findParentInvoicingClashes,
  clashingInvoices,
  isConcurrentlyEdited,
} from '@erp/domain/sync';
import {
  planPaymentCarryOver,
  type CarryOverAllocation,
} from '@erp/domain/payments';
import { buildPurchaseReceptionStockEffects } from '@erp/domain/purchases';
import { normalizeStockQuantity } from '@erp/domain/inventory';
import { DEFAULT_ARTICLE_UNIT } from '@erp/domain/sales';
import { mirrorStorefrontStockForArticles } from './stock-repository';

export class PurchaseNumberConflictError extends Error {
  readonly code = 'PURCHASE_NUMBER_CONFLICT';
  constructor(
    message = 'Purchase document number already used for this supplier',
  ) {
    super(message);
    this.name = 'PurchaseNumberConflictError';
  }
}

/**
 * L'index d'unicité du numéro fournisseur. Deux noms : l'ancien couvrait
 * (fournisseur, numéro), le nouveau (fournisseur, type, numéro) — voir la
 * migration 20260806140000. Les deux restent reconnus parce que le front et la
 * base ne se déploient pas au même instant : entre les deux, une collision
 * remonte encore sous l'ancien nom, et la rater ferait retomber l'utilisateur
 * sur un message générique.
 */
const SUPPLIER_NUMBER_INDEXES = [
  'uq_purchase_docs_supplier_type_number',
  'uq_purchase_docs_supplier_number',
];

function isUniqueViolation(err: unknown, indexNames: string[]): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: string; message?: string };
  if (e.code !== '23505' || typeof e.message !== 'string') return false;
  const message = e.message;
  return indexNames.some((name) => message.includes(name));
}

// Le hop article passe par `authorized_articles` (relation calculée
// `authorized_articles(purchase_document_lines)`, migration 20260826091000) :
// c'est là que vivent les politiques de colonnes par rôle et la visibilité
// des coûts. Lire `articles` en direct les contournait. Même contrat que le
// repo ventes ; `purchase_document*` ne sont pas des tables gouvernées et
// restent lues telles quelles.
const PURCHASE_DOCUMENT_SELECT =
  '*, purchase_document_lines(*, article:authorized_articles(code, name, is_invoiceable, unit))';

/** Colonnes de `PurchaseDocumentSummary`. Toute colonne ajoutée au type DOIT
 *  être ajoutée ici — sinon le champ arrive `undefined` et fausse une somme. */
export const PURCHASE_DOCUMENT_SUMMARY_SELECT =
  'id, number, doc_type, doc_date, supplier_id, is_invoiced, total_ttc, created_at';
// `.in()` filters travel in the GET query string: past a few hundred UUIDs the
// URL blows the gateway's ~32KB cap, so id fan-outs must be chunked.
const IN_FILTER_CHUNK_SIZE = 500;

function sameFiniteNumber(left: number, right: number): boolean {
  return Number.isFinite(left) && Number.isFinite(right) && left === right;
}

function sameParentSet(
  existing: Pick<PurchaseDocument, 'parentDocId' | 'parentDocIds'>,
  input: Pick<CreatePurchaseDocumentInput, 'parentDocId' | 'parentDocIds'>,
): boolean {
  const existingIds = [
    ...new Set([
      ...existing.parentDocIds,
      ...(existing.parentDocId ? [existing.parentDocId] : []),
    ]),
  ].sort();
  const inputIds = [
    ...new Set([
      ...(input.parentDocIds ?? []),
      ...(input.parentDocId ? [input.parentDocId] : []),
    ]),
  ].sort();
  return (
    existingIds.length === inputIds.length &&
    existingIds.every((id, index) => id === inputIds[index])
  );
}

function sameCustomProperties(
  existing: PurchaseDocument['customProperties'],
  input: CreatePurchaseDocumentInput['customProperties'],
): boolean {
  const existingValues = existing ?? {};
  const inputValues = input ?? {};
  const existingKeys = Object.keys(existingValues).sort();
  const inputKeys = Object.keys(inputValues).sort();
  return (
    existingKeys.length === inputKeys.length &&
    existingKeys.every(
      (key, index) =>
        key === inputKeys[index] && existingValues[key] === inputValues[key],
    )
  );
}

/**
 * Reconnaît uniquement le rejeu exact d'une facture déjà persistée.
 *
 * Il ne suffit surtout pas que (fournisseur, type, numéro) soit identique :
 * cette clé décrit aussi un vrai conflit de saisie. Les lignes sont remises
 * dans leur ordre métier explicite (`line_number`) puis comparées une à une,
 * en exigeant une séquence complète 1..N. Ainsi un en-tête commité sans toutes
 * ses lignes ne peut jamais être pris pour un rejeu réussi.
 */
function isExactPurchaseInvoiceReplay(
  existing: PurchaseDocument,
  input: CreatePurchaseDocumentInput,
): boolean {
  if (
    input.docType !== 'invoice' ||
    existing.docType !== 'invoice' ||
    existing.supplierId !== input.supplierId ||
    existing.number !== input.number ||
    existing.docDate !== input.docDate ||
    existing.notes !== (input.notes ?? null) ||
    existing.vatExempt !== (input.vatExempt ?? false) ||
    !sameParentSet(existing, input) ||
    !sameCustomProperties(existing.customProperties, input.customProperties) ||
    !sameFiniteNumber(Number(existing.totalHt), Number(input.totalHt ?? 0)) ||
    !sameFiniteNumber(Number(existing.totalVat), Number(input.totalVat ?? 0)) ||
    !sameFiniteNumber(Number(existing.totalTtc), Number(input.totalTtc ?? 0)) ||
    !sameFiniteNumber(
      Number(existing.withholdingAmount),
      Number(input.withholdingAmount ?? 0),
    ) ||
    !sameFiniteNumber(
      Number(existing.stampAmount),
      Number(input.stampAmount ?? 0),
    ) ||
    !sameFiniteNumber(Number(existing.netToPay), Number(input.netToPay ?? 0))
  ) {
    return false;
  }

  const existingLines = [...(existing.lines ?? [])].sort(
    (left, right) =>
      left.lineNumber - right.lineNumber || left.id.localeCompare(right.id),
  );
  if (existingLines.length !== input.lines.length) return false;

  return existingLines.every((line, index) => {
    const requested = input.lines[index];
    if (!requested || line.lineNumber !== index + 1) return false;
    return (
      line.articleId === (requested.articleId ?? null) &&
      (line.variantId ?? null) === (requested.variantId ?? null) &&
      (line.variantLabel ?? null) === (requested.variantLabel ?? null) &&
      line.description === requested.description &&
      line.articleUnit === (requested.unit ?? null) &&
      line.sourceDocId === (requested.sourceDocId ?? null) &&
      sameFiniteNumber(Number(line.quantity), Number(requested.quantity)) &&
      sameFiniteNumber(
        Number(line.unitPriceHt),
        Number(requested.unitPriceHt),
      ) &&
      sameFiniteNumber(
        Number(line.purchasePrice),
        Number(requested.unitPriceHt ?? 0),
      ) &&
      sameFiniteNumber(
        Number(line.discountPct),
        Number(requested.discountPct ?? 0),
      ) &&
      sameFiniteNumber(Number(line.vatRate), Number(requested.vatRate)) &&
      sameFiniteNumber(Number(line.totalHt), Number(requested.totalHt ?? 0)) &&
      sameFiniteNumber(Number(line.totalTtc), Number(requested.totalTtc ?? 0))
    );
  });
}

interface PurchaseReceptionSaveResult {
  document_id: string;
  affected_article_ids?: string[];
  created: boolean;
}

interface PurchaseInvoiceCreateResult {
  document_id: string;
  created: boolean;
}

function isPurchaseInvoiceParentConflict(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const databaseError = error as { code?: string; message?: string };
  return (
    databaseError.code === '23514' &&
    databaseError.message?.includes(
      'purchase_invoice_parent_already_invoiced',
    ) === true
  );
}

function newPurchaseRequestId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  // React Native ne fournit pas toujours `crypto.randomUUID`. L'identifiant
  // ne porte aucun secret : il sert uniquement de clé de rejeu UUID v4.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    (character) => {
      const value = Math.floor(Math.random() * 16);
      return (character === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    },
  );
}

export class SupabasePurchaseRepository implements IPurchaseRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  /**
   * Résout l'unité catalogue depuis la projection autorisée, puis la fige sur
   * la ligne. Les snapshots d'un BR existant gagnent sur le catalogue actuel :
   * changer ensuite l'unité de l'article ne réécrit pas l'histoire du stock.
   */
  private async resolveReceptionLineUnits(
    lines: readonly CreatePurchaseLineInput[],
    existingLines: readonly PurchaseDocumentLine[] = [],
  ): Promise<CreatePurchaseLineInput[]> {
    const unitsByArticle = new Map<string, string | null>();
    for (const line of existingLines) {
      if (line.articleId && !unitsByArticle.has(line.articleId)) {
        // Legacy articles allowed a NULL unit while every article-facing mapper
        // interpreted that value as the default `pcs`. Freeze the same trusted
        // default here so a valid old receipt does not become impossible to
        // save merely because its catalogue row predates explicit units.
        unitsByArticle.set(
          line.articleId,
          line.articleUnit ?? DEFAULT_ARTICLE_UNIT,
        );
      }
    }

    const unresolvedIds = [
      ...new Set(
        lines
          .map((line) => line.articleId)
          .filter(
            (articleId): articleId is string =>
              !!articleId && !unitsByArticle.has(articleId),
          ),
      ),
    ];
    for (
      let offset = 0;
      offset < unresolvedIds.length;
      offset += IN_FILTER_CHUNK_SIZE
    ) {
      const ids = unresolvedIds.slice(offset, offset + IN_FILTER_CHUNK_SIZE);
      const { data, error } = await (this.supabase as any)
        .from('authorized_articles')
        .select('id, unit')
        .in('id', ids);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        unitsByArticle.set(
          row['id'] as string,
          (row['unit'] as string | null | undefined) ?? DEFAULT_ARTICLE_UNIT,
        );
      }
    }

    const missing = unresolvedIds.find(
      (articleId) => !unitsByArticle.has(articleId),
    );
    if (missing) {
      throw new Error('purchase_reception_article_unit_unavailable');
    }

    return lines.map((line) => ({
      ...line,
      unit: line.articleId
        ? (unitsByArticle.get(line.articleId) ?? DEFAULT_ARTICLE_UNIT)
        : (line.unit ?? null),
    }));
  }

  async listDocuments(
    filters?: PurchaseDocumentFilters,
  ): Promise<PurchaseDocument[]> {
    if (filters?.lineArticleId) return this.listDocumentsByLineArticle(filters);

    // Lignes embarquées à la demande uniquement — même contrat que le repo
    // ventes. La jointure lignes+article sur CHAQUE ligne de liste coûtait
    // 320-720 ms par requête pour des pages qui n'affichent que les totaux.
    const select = filters?.includeLines ? PURCHASE_DOCUMENT_SELECT : '*';
    const buildQuery = () => {
      let query = (this.supabase as any)
        .from('purchase_documents')
        .select(select);
      query = applyPurchaseDocumentFilters(query, filters);
      return applyPurchaseDocumentSort(query, filters?.sort);
    };
    let query = buildQuery();
    const hasExplicitLimit = Boolean(filters?.limit && filters.limit > 0);
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }

    const { data, error } = await query;
    if (error) throw error;
    const firstRows = (data ?? []) as Array<Record<string, unknown>>;
    const rows = hasExplicitLimit
      ? firstRows
      : await completeCappedRows(firstRows, (from, to) =>
          buildQuery().range(from, to),
        );
    return rows.map(mapPurchaseDocument);
  }

  /**
   * Documents containing at least one line for `filters.lineArticleId`.
   *
   * Résolu par les LIGNES : l'index `idx_purchase_lines_article` rend les ids
   * de documents, puis les documents arrivent par lots PARALLÈLES. Voir le
   * commentaire jumeau du repo ventes pour la raison de ne pas partir des
   * documents avec un embed `!inner`.
   *
   * ⚠ `includeLines` rapatrie ici TOUTES les lignes des documents trouvés,
   * pas seulement celles de l'article : la fiche article passe par
   * `listLinesByArticle`.
   */
  private async listDocumentsByLineArticle(
    filters: PurchaseDocumentFilters,
  ): Promise<PurchaseDocument[]> {
    const docIds = await this.getDocumentIdsForLineArticle(
      filters.lineArticleId!,
    );
    if (docIds.length === 0) return [];

    const select = filters.includeLines ? PURCHASE_DOCUMENT_SELECT : '*';
    const pages = await Promise.all(
      chunkIds(docIds, IN_FILTER_CHUNK_SIZE).map((chunk) =>
        applyPurchaseDocumentFilters(
          (this.supabase as any)
            .from('purchase_documents')
            .select(select)
            .in('id', chunk),
          filters,
        ),
      ),
    );

    const docs: PurchaseDocument[] = [];
    for (const { data, error } of pages as Array<{
      data: unknown;
      error: unknown;
    }>) {
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>)
        docs.push(mapPurchaseDocument(row));
    }

    // Chunked fetches lose server ordering — restore the default newest-first.
    docs.sort(compareDocumentsNewestFirst);
    const offset = Math.max(0, filters.offset ?? 0);
    return filters.limit && filters.limit > 0
      ? docs.slice(offset, offset + filters.limit)
      : docs.slice(offset);
  }

  private async getDocumentIdsForLineArticle(
    articleId: string,
  ): Promise<string[]> {
    const rows = await fetchAllRows<{ document_id: string }>((from, to) =>
      (this.supabase as any)
        .from('purchase_document_lines')
        .select('document_id')
        .eq('article_id', articleId)
        .order('id', { ascending: true })
        .range(from, to),
    );
    return Array.from(new Set(rows.map((row) => row.document_id)));
  }

  /** Toutes les lignes de cet article — voir `IPurchaseRepository.listLinesByArticle`. */
  async listLinesByArticle(articleId: string): Promise<PurchaseDocumentLine[]> {
    if (!articleId) return [];
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      (this.supabase as any)
        .from('purchase_document_lines')
        .select(
          '*, article:authorized_articles(code, name, is_invoiceable, unit)',
        )
        .eq('article_id', articleId)
        // `id` en dernier rend l'ordre TOTAL — voir withStableOrder.
        .order('line_number', { ascending: true })
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map(mapPurchaseLine);
  }

  /**
   * Liste projetée pour le tableau de bord — voir `PurchaseDocumentSummary`.
   * Huit colonnes, et surtout PAS les lignes : `PURCHASE_DOCUMENT_SELECT`
   * embarque `purchase_document_lines(*)` avec une jointure article par ligne.
   */
  async listDocumentSummaries(
    filters?: PurchaseDocumentFilters,
  ): Promise<PurchaseDocumentSummary[]> {
    if (filters?.lineArticleId) {
      throw new Error(
        'listDocumentSummaries: lineArticleId needs the resolution path of listDocuments',
      );
    }
    const buildQuery = () => {
      let query = (this.supabase as any)
        .from('purchase_documents')
        .select(PURCHASE_DOCUMENT_SUMMARY_SELECT);
      query = applyPurchaseDocumentFilters(query, filters);
      return applyPurchaseDocumentSort(query, filters?.sort);
    };
    let query = buildQuery();
    const hasExplicitLimit = Boolean(filters?.limit && filters.limit > 0);
    if (filters?.limit && filters.limit > 0) {
      const offset = Math.max(0, filters.offset ?? 0);
      query = query.range(offset, offset + filters.limit - 1);
    }
    const { data, error } = await query;
    if (error) throw error;
    const firstRows = (data ?? []) as Array<Record<string, unknown>>;
    const rows = hasExplicitLimit
      ? firstRows
      : await completeCappedRows(firstRows, (from, to) =>
          buildQuery().range(from, to),
        );
    return rows.map(mapPurchaseDocumentSummary);
  }

  /**
   * Empreinte de l'ensemble — voir `DocumentWindowSignature`.
   *
   * UNE requête pour les deux mesures : `count: 'exact'` fait voyager le nombre
   * dans l'en-tête `Content-Range`, et `order(updated_at desc).limit(1)` rend la
   * ligne la plus récemment touchée. Le corps fait une ligne d'une colonne.
   *
   * ⚠ Volontairement PAS `head: true` : on a besoin du corps pour lire
   * `updated_at`. Le compte seul ne suffit pas — une suppression suivie d'une
   * création dans la même minute le laisserait identique.
   */
  async listDocumentsSignature(
    filters?: PurchaseDocumentFilters,
  ): Promise<DocumentWindowSignature> {
    let query = (this.supabase as any)
      .from('purchase_documents')
      .select('updated_at', { count: 'exact' });
    query = applyPurchaseDocumentFilters(query, filters);
    query = query
      .order('updated_at', { ascending: false, nullsFirst: false })
      .limit(1);
    const { data, count, error } = await query;
    if (error) throw error;
    const rows = (data ?? []) as Array<Record<string, unknown>>;
    return {
      count: count ?? 0,
      maxUpdatedAt: (rows[0]?.['updated_at'] as string | undefined) ?? null,
    };
  }

  async countDocuments(filters?: PurchaseDocumentFilters): Promise<number> {
    if (filters?.lineArticleId) {
      const docIds = await this.getDocumentIdsForLineArticle(
        filters.lineArticleId,
      );
      if (docIds.length === 0) return 0;
      let total = 0;
      for (let i = 0; i < docIds.length; i += IN_FILTER_CHUNK_SIZE) {
        const chunk = docIds.slice(i, i + IN_FILTER_CHUNK_SIZE);
        let query = (this.supabase as any)
          .from('purchase_documents')
          .select('id', { count: 'exact', head: true })
          .in('id', chunk);
        query = applyPurchaseDocumentFilters(query, filters);
        const { count, error } = await query;
        if (error) throw error;
        total += count ?? 0;
      }
      return total;
    }

    let query = this.supabase
      .from('purchase_documents')
      .select('id', { count: 'exact', head: true });
    query = applyPurchaseDocumentFilters(query, filters);
    const { count, error } = await query;
    if (error) throw error;
    return count ?? 0;
  }

  /** Projection minimale factures/avoirs pour les soldes fournisseurs —
   *  mêmes 6 colonnes que le repo ventes, paginée au-delà du cap PostgREST. */
  async listBalanceRows(): Promise<DocumentBalanceRow[]> {
    type Row = {
      id: string;
      supplier_id: string;
      doc_type: 'invoice' | 'credit_note';
      total_ttc: number | null;
      net_to_pay: number | null;
      stamp_amount: number | null;
    };
    const rows = await fetchAllRows<Row>((from, to) =>
      (this.supabase as any)
        .from('purchase_documents')
        .select(
          'id, supplier_id, doc_type, total_ttc, net_to_pay, stamp_amount',
        )
        .in('doc_type', ['invoice', 'credit_note'])
        .order('id', { ascending: true })
        .range(from, to),
    );
    return rows.map((r) => ({
      id: r.id,
      thirdPartyId: r.supplier_id,
      docType: r.doc_type,
      totalTtc: Number(r.total_ttc) || 0,
      netToPay: Number(r.net_to_pay) || 0,
      stampAmount: Number(r.stamp_amount) || 0,
    }));
  }

  async getDocumentById(
    id: string,
    allowedTypes?: readonly PurchaseDocType[],
  ): Promise<PurchaseDocument | null> {
    if (allowedTypes?.length === 0) return null;
    let query = (this.supabase as any)
      .from('purchase_documents')
      .select(
        '*, purchase_document_lines(*, article:authorized_articles(code, name, is_invoiceable, unit))',
      )
      .eq('id', id);
    if (allowedTypes) query = query.in('doc_type', allowedTypes);
    const { data, error } = await query.single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapPurchaseDocument(data);
  }

  /**
   * Persiste un BR, ses lignes et son effet de stock dans UNE transaction.
   * Le domaine construit les effets ; la fonction SQL ne fait que vérifier
   * leur concordance structurelle avec les lignes et écrire l'ensemble.
   */
  private async savePurchaseReceptionAtomic(
    documentId: string,
    input: CreatePurchaseDocumentInput,
    isUpdate: boolean,
    baseUpdatedAt?: string,
  ): Promise<PurchaseDocument> {
    if (!input.warehouseId) {
      throw new Error('purchase_reception_warehouse_required');
    }
    if (!isUpdate && !input.clientRequestId) {
      throw new Error('purchase_reception_client_request_id_required');
    }

    const stockEffects = buildPurchaseReceptionStockEffects(input.lines);
    const parentDocIds =
      input.parentDocIds ?? (input.parentDocId ? [input.parentDocId] : []);
    const lines = input.lines.map((line, index) => ({
      line_number: index + 1,
      article_id: line.articleId ?? null,
      variant_id: line.variantId ?? null,
      variant_label: line.variantLabel ?? null,
      description: line.description,
      quantity: normalizeStockQuantity(line.quantity),
      unit: line.unit ?? null,
      unit_price_ht: line.unitPriceHt,
      purchase_price: line.unitPriceHt,
      discount_pct: line.discountPct ?? 0,
      vat_rate: line.vatRate,
      total_ht: line.totalHt ?? 0,
      total_ttc: line.totalTtc ?? 0,
      source_doc_id: line.sourceDocId ?? null,
    }));

    const { data, error } = await this.supabase.rpc(
      'save_purchase_reception_atomic',
      {
        p_document_id: documentId,
        p_client_request_id: input.clientRequestId ?? null,
        p_is_update: isUpdate,
        p_number: input.number,
        p_supplier_id: input.supplierId,
        p_doc_date: input.docDate,
        p_warehouse_id: input.warehouseId,
        p_parent_doc_id: input.parentDocId ?? null,
        p_parent_doc_ids: parentDocIds,
        p_notes: input.notes ?? null,
        p_custom_properties: input.customProperties ?? {},
        p_vat_exempt: input.vatExempt ?? false,
        p_total_ht: input.totalHt ?? 0,
        p_total_vat: input.totalVat ?? 0,
        p_total_ttc: input.totalTtc ?? 0,
        p_withholding_amount: input.withholdingAmount ?? 0,
        p_stamp_amount: input.stampAmount ?? 0,
        p_net_to_pay: input.netToPay ?? 0,
        p_lines: lines,
        p_stock_effects: stockEffects.map((effect) => ({
          article_id: effect.articleId,
          quantity: effect.quantity,
        })),
        p_base_updated_at: baseUpdatedAt ?? null,
      },
    );
    if (error) {
      if (isUniqueViolation(error, SUPPLIER_NUMBER_INDEXES)) {
        if (isUpdate) throw new PurchaseNumberConflictError();
        throw await this.describeNumberClash(input);
      }
      throw error;
    }

    const result = data as unknown as PurchaseReceptionSaveResult | null;
    const savedId = result?.document_id ?? documentId;
    const affectedArticleIds =
      result?.affected_article_ids ??
      stockEffects.map((effect) => effect.articleId);
    if (affectedArticleIds.length > 0) {
      // Le grand-livre est déjà commité. Si le miroir boutique échoue, le
      // même clientRequestId permet de rejouer sans doubler le stock puis de
      // retenter uniquement ce rafraîchissement dérivé.
      await mirrorStorefrontStockForArticles(
        this.supabase,
        affectedArticleIds,
        { throwOnError: true },
      );
    }
    const saved = await this.getDocumentById(savedId);
    if (!saved) throw new Error('purchase_reception_not_created');
    return saved;
  }

  private async ensureVariantLineSchema(lines: CreatePurchaseDocumentInput['lines'] | undefined): Promise<void> {
    if (!lines?.some((line) => line.variantId)) return;
    const { error } = await this.supabase.from('purchase_document_lines').select('id,variant_id,variant_label').limit(0);
    if (error) throw error;
  }

  async createDocument(
    input: CreatePurchaseDocumentInput,
  ): Promise<PurchaseDocument> {
    await this.ensureVariantLineSchema(input.lines);
    return this.createDocumentInternal(input);
  }

  async importHistoricalReceptionSnapshot(
    input: CreatePurchaseDocumentInput & { docType: 'reception' },
  ): Promise<PurchaseDocument> {
    await this.assertParentsNotAlreadyInvoiced(input);
    const resolvedLines = await this.resolveReceptionLineUnits(input.lines);
    buildPurchaseReceptionStockEffects(resolvedLines);
    // L'id est fourni par l'appelant à la transaction et sert d'identité au
    // rejeu d'une même ligne d'import. Aucun mouvement de stock n'est créé.
    const documentId = newPurchaseRequestId();
    const parentDocIds =
      input.parentDocIds ?? (input.parentDocId ? [input.parentDocId] : []);
    const { data, error } = await this.supabase.rpc(
      'import_historical_purchase_reception_snapshot',
      {
        p_document_id: documentId,
        p_number: input.number,
        p_supplier_id: input.supplierId,
        p_doc_date: input.docDate,
        p_parent_doc_id: input.parentDocId ?? null,
        p_parent_doc_ids: parentDocIds,
        p_notes: input.notes ?? null,
        p_custom_properties: input.customProperties ?? {},
        p_vat_exempt: input.vatExempt ?? false,
        p_total_ht: input.totalHt ?? 0,
        p_total_vat: input.totalVat ?? 0,
        p_total_ttc: input.totalTtc ?? 0,
        p_withholding_amount: input.withholdingAmount ?? 0,
        p_stamp_amount: input.stampAmount ?? 0,
        p_net_to_pay: input.netToPay ?? 0,
        p_lines: resolvedLines.map((line, index) => ({
          line_number: index + 1,
          article_id: line.articleId ?? null,
          variant_id: line.variantId ?? null,
          variant_label: line.variantLabel ?? null,
          description: line.description,
          quantity: line.quantity,
          unit: line.unit ?? null,
          unit_price_ht: line.unitPriceHt,
          purchase_price: line.unitPriceHt,
          discount_pct: line.discountPct ?? 0,
          vat_rate: line.vatRate,
          total_ht: line.totalHt ?? 0,
          total_ttc: line.totalTtc ?? 0,
          source_doc_id: line.sourceDocId ?? null,
        })),
      },
    );
    if (error) {
      if (isUniqueViolation(error, SUPPLIER_NUMBER_INDEXES)) {
        throw await this.describeNumberClash(input);
      }
      throw error;
    }
    const result = data as unknown as { document_id?: string } | null;
    const saved = await this.getDocumentById(result?.document_id ?? documentId);
    if (!saved) throw new Error('purchase_reception_import_not_created');
    return saved;
  }

  async reconcileLegacyReceptionStock(
    id: string,
    warehouseId: string,
    clientRequestId: string,
  ): Promise<PurchaseDocument> {
    const current = await this.getDocumentById(id);
    if (!current || current.docType !== 'reception') {
      throw new Error('purchase_reception_not_found');
    }
    const postingMode =
      current.stockPostingMode ??
      (current.warehouseId ? 'live' : 'legacy_untracked');
    if (postingMode !== 'legacy_untracked') {
      throw new Error('purchase_reception_legacy_reconciliation_forbidden');
    }
    const lines = await this.resolveReceptionLineUnits(
      (current.lines ?? []).map((line) => ({
        articleId: line.articleId ?? undefined,
        variantId: line.variantId ?? null,
        variantLabel: line.variantLabel ?? null,
        description: line.description,
        quantity: line.quantity,
        unit: line.articleUnit,
        unitPriceHt: line.unitPriceHt,
        discountPct: line.discountPct,
        vatRate: line.vatRate,
        totalHt: line.totalHt,
        totalTtc: line.totalTtc,
        sourceDocId: line.sourceDocId,
      })),
      current.lines ?? [],
    );
    return this.savePurchaseReceptionAtomic(
      id,
      {
        docType: 'reception',
        number: current.number,
        supplierId: current.supplierId,
        warehouseId,
        clientRequestId,
        docDate: current.docDate,
        parentDocId: current.parentDocId ?? undefined,
        parentDocIds: current.parentDocIds,
        notes: current.notes ?? undefined,
        customProperties: current.customProperties,
        vatExempt: current.vatExempt,
        totalHt: current.totalHt,
        totalVat: current.totalVat,
        totalTtc: current.totalTtc,
        withholdingAmount: current.withholdingAmount,
        stampAmount: current.stampAmount,
        netToPay: current.netToPay,
        lines,
      },
      true,
    );
  }

  private async createDocumentInternal(
    input: CreatePurchaseDocumentInput,
  ): Promise<PurchaseDocument> {
    if (input.clientRequestId && (input.docType === 'order' || input.docType === 'credit_note' || input.docType === 'reception')) {
      const replay = await resumePurchaseDraftMutation(this.supabase, null, input);
      if (input.docType === 'reception' && !input.warehouseId && !replay) {
        throw new Error('purchase_reception_warehouse_required');
      }
      const full = input.docType === 'reception' && !replay
        ? { ...input, lines: await this.resolveReceptionLineUnits(input.lines) } : input;
      const savedId = replay ?? await savePurchaseDraftMutation(this.supabase, null, input, full);
      const saved = await this.getDocumentById(savedId);
      if (!saved) throw new Error('purchase_document_not_created');
      if (input.docType === 'reception') {
        void mirrorStorefrontStockForArticles(this.supabase, [...new Set(input.lines.map(line => line.articleId).filter((articleId): articleId is string => Boolean(articleId)))]).catch(() => undefined);
      }
      return saved;
    }

    if (input.docType === 'invoice' && input.clientRequestId) {
      const completed = await resumeInvoicePaymentCheckout(this.supabase, 'purchases', input);
      if (completed) {
        const saved = await this.getDocumentById(completed);
        if (!saved) throw new Error('invoice_payment_checkout_resource_unavailable');
        return saved;
      }
      // A new frozen validation key must never adopt an unrelated older
      // invoice merely because supplier/number/lines happen to be identical.
      await this.assertParentsNotAlreadyInvoiced(input);
      return this.createPurchaseInvoiceAtomic(input);
    }

    // Une première tentative peut avoir créé la facture, puis l'appelant peut
    // avoir perdu la réponse en réconciliant le BR parent. Reconnaître ce rejeu
    // AVANT le contrôle anti-double-facturation évite que la facture déjà
    // présente soit prise pour sa propre concurrente.
    const existingInvoice = await this.findExactInvoiceReplay(input);
    if (existingInvoice)
      return this.completePurchaseInvoice(existingInvoice, input);

    // Un bon de réception ne doit pas être facturé deux fois — même règle et
    // même scénario que côté ventes (poste hors-ligne + poste connecté).
    try {
      await this.assertParentsNotAlreadyInvoiced(input);
    } catch (error) {
      // Course : une transaction identique a pu apparaître entre la prélecture
      // ci-dessus et le contrôle des parents. On ne la reconnaît qu'avec la
      // même empreinte complète, jamais avec le seul numéro.
      if (error instanceof ParentAlreadyInvoicedError) {
        const racedReplay = await this.findExactInvoiceReplay(input);
        if (racedReplay)
          return this.completePurchaseInvoice(racedReplay, input);
      }
      throw error;
    }

    if (input.docType === 'reception') {
      if (!input.warehouseId) {
        throw new Error('purchase_reception_warehouse_required');
      }
      if (!input.clientRequestId) {
        throw new Error('purchase_reception_client_request_id_required');
      }
      const resolvedLines = await this.resolveReceptionLineUnits(input.lines);
      return this.savePurchaseReceptionAtomic(
        input.clientRequestId,
        { ...input, lines: resolvedLines },
        false,
      );
    }

    if (input.docType === 'invoice') {
      return this.createPurchaseInvoiceAtomic(input);
    }

    const parentDocIds =
      input.parentDocIds ?? (input.parentDocId ? [input.parentDocId] : []);

    const { data: doc, error: docError } = await this.supabase
      .from('purchase_documents')
      .insert({
        doc_type: input.docType,
        number: input.number,
        supplier_id: input.supplierId,
        warehouse_id: null,
        client_request_id: null,
        stock_posting_mode: 'live',
        doc_date: input.docDate,
        parent_doc_id: input.parentDocId,
        parent_doc_ids: parentDocIds,
        notes: input.notes,
        custom_properties: input.customProperties ?? {},
        // Cast: vat_exempt (migration 20260719130000) n'est pas encore dans le
        // database.types.ts généré. `supabase gen types typescript` pour le
        // rafraîchir et retirer ce cast.
        vat_exempt: input.vatExempt ?? false,
        total_ht: input.totalHt ?? 0,
        total_vat: input.totalVat ?? 0,
        total_ttc: input.totalTtc ?? 0,
        withholding_amount: input.withholdingAmount ?? 0,
        stamp_amount: input.stampAmount ?? 0,
        net_to_pay: input.netToPay ?? 0,
      } as never)
      .select()
      .single();
    if (docError) {
      if (isUniqueViolation(docError, SUPPLIER_NUMBER_INDEXES)) {
        throw await this.describeNumberClash(input);
      }
      throw docError;
    }

    if (input.lines.length > 0) {
      const lines = input.lines.map((line, idx) => ({
        document_id: doc.id,
        line_number: idx + 1,
        article_id: line.articleId,
        variant_id: line.variantId ?? null,
        variant_label: line.variantLabel ?? null,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit ?? null,
        unit_price_ht: line.unitPriceHt,
        purchase_price: line.unitPriceHt ?? 0,
        discount_pct: line.discountPct ?? 0,
        vat_rate: line.vatRate,
        total_ht: line.totalHt ?? 0,
        total_ttc: line.totalTtc ?? 0,
        // Facture d'achat groupée : la ligne garde le BR dont elle est reprise,
        // seule façon de la réattribuer sans deviner (mig 20260803110000).
        source_doc_id: line.sourceDocId ?? null,
      }));

      const { error: linesError } = await this.supabase
        .from('purchase_document_lines')
        .insert(lines);
      if (linesError) throw linesError;
    }

    return (await this.getDocumentById(doc.id))!;
  }

  /**
   * Crée en une transaction l'en-tête, toutes les lignes et le marqueur des
   * documents sources. La reprise des règlements reste ensuite un effet dérivé
   * best-effort : elle ne peut plus laisser une demi-facture en base.
   */
  private async createPurchaseInvoiceAtomic(
    input: CreatePurchaseDocumentInput,
  ): Promise<PurchaseDocument> {
    const documentId = newPurchaseRequestId();
    const parentDocIds =
      input.parentDocIds ?? (input.parentDocId ? [input.parentDocId] : []);
    const lines = input.lines.map((line, index) => ({
      line_number: index + 1,
      article_id: line.articleId ?? null,
      variant_id: line.variantId ?? null,
      variant_label: line.variantLabel ?? null,
      description: line.description,
      quantity: line.quantity,
      unit: line.unit ?? null,
      unit_price_ht: line.unitPriceHt,
      purchase_price: line.unitPriceHt,
      discount_pct: line.discountPct ?? 0,
      vat_rate: line.vatRate,
      total_ht: line.totalHt ?? 0,
      total_ttc: line.totalTtc ?? 0,
      source_doc_id: line.sourceDocId ?? null,
    }));

    if (input.clientRequestId) {
      const { data: sourceRows, error: sourceError } = parentDocIds.length
        ? await this.supabase.from('payment_allocations')
            .select('id, payment_id, document_id, allocated_amount')
            .in('document_id', parentDocIds)
        : { data: [], error: null };
      if (sourceError) throw sourceError;
      const sourceAllocations = (sourceRows ?? []).map((row) => ({
        id: row.id, paymentId: row.payment_id, documentId: row.document_id,
        allocatedAmount: Number(row.allocated_amount),
      })).sort((a, b) => a.id.localeCompare(b.id));
      const { transfers } = planPaymentCarryOver({ sourceAllocations,
        targetDue: input.netToPay ?? input.totalTtc ?? 0, targetAlreadyAllocated: 0 });
      const savedId = await createInvoicePaymentCheckout(this.supabase, 'purchases', input, {
        document: {
          doc_type: 'invoice', number: input.number, supplier_id: input.supplierId,
          doc_date: input.docDate, parent_doc_id: input.parentDocId ?? null,
          parent_doc_ids: parentDocIds, notes: input.notes ?? null,
          custom_properties: input.customProperties ?? {}, vat_exempt: input.vatExempt ?? false,
          total_ht: input.totalHt ?? 0, total_vat: input.totalVat ?? 0,
          total_ttc: input.totalTtc ?? 0, withholding_amount: input.withholdingAmount ?? 0,
          stamp_amount: input.stampAmount ?? 0, net_to_pay: input.netToPay ?? input.totalTtc ?? 0,
        }, lines, transfers: transfers.map((transfer) => ({
          allocation_id: transfer.allocationId, payment_id: transfer.paymentId,
          moved_amount: transfer.movedAmount, remaining_on_source: transfer.remainingOnSource,
        })),
      });
      const saved = await this.getDocumentById(savedId);
      if (!saved) throw new Error('invoice_payment_checkout_resource_unavailable');
      return saved;
    }

    const { data, error } = await this.supabase.rpc(
      'create_purchase_invoice_atomic',
      {
        p_document_id: documentId,
        p_number: input.number,
        p_supplier_id: input.supplierId,
        p_doc_date: input.docDate,
        p_parent_doc_id: input.parentDocId ?? null,
        p_parent_doc_ids: parentDocIds,
        p_notes: input.notes ?? null,
        p_custom_properties: input.customProperties ?? {},
        p_vat_exempt: input.vatExempt ?? false,
        p_total_ht: input.totalHt ?? 0,
        p_total_vat: input.totalVat ?? 0,
        p_total_ttc: input.totalTtc ?? 0,
        p_withholding_amount: input.withholdingAmount ?? 0,
        p_stamp_amount: input.stampAmount ?? 0,
        p_net_to_pay: input.netToPay ?? 0,
        p_lines: lines,
      },
    );

    if (error) {
      if (isUniqueViolation(error, SUPPLIER_NUMBER_INDEXES)) {
        // Une réponse perdue ou une création concurrente identique retombe sur
        // la clé fournisseur/type/numéro. Seule l'empreinte complète autorise
        // le retour du document existant.
        const exactReplay = await this.findExactInvoiceReplay(input);
        if (exactReplay)
          return this.completePurchaseInvoice(exactReplay, input);
        throw await this.describeNumberClash(input);
      }
      if (isPurchaseInvoiceParentConflict(error)) {
        // La fonction vérifie les parents sous verrou avant l'INSERT : une
        // transaction identique gagnante peut donc se manifester par ce code
        // avant même la contrainte de numéro.
        const exactReplay = await this.findExactInvoiceReplay(input);
        if (exactReplay)
          return this.completePurchaseInvoice(exactReplay, input);
        // Reconstruit l'erreur métier détaillée et localisable (numéros des BR
        // et factures en conflit) à partir de l'état désormais commité.
        await this.assertParentsNotAlreadyInvoiced({
          ...input,
          allowDuplicateParentInvoice: false,
        });
      }
      throw error;
    }

    const result = data as unknown as PurchaseInvoiceCreateResult | null;
    const saved = await this.getDocumentById(result?.document_id ?? documentId);
    if (!saved) throw new Error('purchase_invoice_not_created');
    return this.completePurchaseInvoice(saved, input);
  }

  private async completePurchaseInvoice(
    document: PurchaseDocument,
    input: CreatePurchaseDocumentInput,
  ): Promise<PurchaseDocument> {
    const parentDocIds = [
      ...new Set(
        [
          ...(input.parentDocIds ?? []),
          ...(input.parentDocId ? [input.parentDocId] : []),
        ].filter(Boolean),
      ),
    ];
    await this.carryOverParentPayments(
      document.id,
      parentDocIds,
      Number(input.netToPay ?? input.totalTtc ?? 0),
    );
    return document;
  }

  private async findExactInvoiceReplay(
    input: CreatePurchaseDocumentInput,
  ): Promise<PurchaseDocument | null> {
    if (input.docType !== 'invoice') return null;

    // Pas d'enrichissement catalogue ici : l'unité comparée est le snapshot
    // brut de la ligne, exactement celui écrit par createDocumentInternal.
    const { data, error } = await (this.supabase as any)
      .from('purchase_documents')
      .select('*, purchase_document_lines(*)')
      .eq('supplier_id', input.supplierId)
      .eq('doc_type', 'invoice')
      .eq('number', input.number)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;

    const existing = mapPurchaseDocument(data as Record<string, unknown>);
    return isExactPurchaseInvoiceReplay(existing, input) ? existing : null;
  }

  /**
   * Reporte sur la facture d'achat les affectations de règlement de ses
   * documents sources (bons de réception, commandes fournisseur).
   *
   * Jumeau de `SupabaseSalesRepository.carryOverParentPayments`, y compris dans
   * ce qu'il refuse de faire : le plan est du domaine pur
   * (`planPaymentCarryOver`), ici on ne fait qu'écrire, et on ne crée jamais de
   * montant — le timbre fiscal, absent du BR, laisse donc une facture née d'un
   * BR soldé « partiellement payée », ce qui est exact.
   *
   * ⚠ Aujourd'hui, l'écran de règlement n'affecte QUE des factures d'achat
   * (`PURCHASE_PAYABLE`), donc ce report ne trouve normalement rien à déplacer.
   * Il est là pour l'inverse du cas ventes : le jour où un BR devient réglable
   * — ou qu'un import en pose un —, la facture doit hériter, pas repartir de
   * zéro. Le manque exact avait coûté 33 factures fausses côté ventes.
   *
   * Jamais fatal : une facture correctement créée ne doit pas être rejetée
   * parce que le report a échoué — le lien reste réparable, l'affectation étant
   * toujours sur son BR.
   */
  private async carryOverParentPayments(
    invoiceId: string,
    parentIds: string[],
    netToPay: number,
  ): Promise<void> {
    const sources = [...new Set(parentIds.filter(Boolean))];
    if (sources.length === 0 || !(netToPay > 0)) return;

    try {
      const { data, error } = await this.supabase
        .from('payment_allocations')
        .select('id, payment_id, document_id, allocated_amount')
        // Inclure la cible rend la reprise idempotente : un rejeu exact après
        // réponse perdue ne doit déplacer que le reste réellement dû.
        .in('document_id', [...sources, invoiceId]);
      if (error) throw error;

      const rows = (data ?? []) as Array<Record<string, unknown>>;
      const targetAlreadyAllocated = rows
        .filter((row) => row['document_id'] === invoiceId)
        .reduce(
          (total, row) => total + Number(row['allocated_amount'] ?? 0),
          0,
        );

      // Ordre stable : le plan plafonne à la dette de la facture, donc « qui est
      // repris en premier » doit être reproductible d'un poste à l'autre.
      const sourceAllocations: CarryOverAllocation[] = rows
        .filter((row) => sources.includes(row['document_id'] as string))
        .map((row) => ({
          id: row['id'] as string,
          paymentId: row['payment_id'] as string,
          documentId: row['document_id'] as string,
          allocatedAmount: Number(row['allocated_amount'] ?? 0),
        }))
        .sort((a, b) => a.id.localeCompare(b.id));
      if (sourceAllocations.length === 0) return;

      const { transfers } = planPaymentCarryOver({
        sourceAllocations,
        targetDue: netToPay,
        targetAlreadyAllocated,
      });

      for (const transfer of transfers) {
        if (transfer.remainingOnSource === 0) {
          // La ligne part entière : un simple déménagement, aucun montant touché.
          const { error: moveErr } = await this.supabase
            .from('payment_allocations')
            .update({
              document_id: invoiceId,
              document_type: 'invoice',
            } as never)
            .eq('id', transfer.allocationId);
          if (moveErr) throw moveErr;
          continue;
        }

        // Reprise partielle : le BR garde le surplus (marchandises écartées de
        // la facture), la facture reçoit sa part.
        const { error: shrinkErr } = await this.supabase
          .from('payment_allocations')
          .update({ allocated_amount: transfer.remainingOnSource } as never)
          .eq('id', transfer.allocationId);
        if (shrinkErr) throw shrinkErr;

        const { error: insertErr } = await this.supabase
          .from('payment_allocations')
          .insert({
            payment_id: transfer.paymentId,
            document_type: 'invoice',
            document_id: invoiceId,
            allocated_amount: transfer.movedAmount,
          } as never);
        if (insertErr) throw insertErr;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        '[payment-carry-over] failed for purchase invoice',
        invoiceId,
        err,
      );
    }
  }

  /**
   * Construit l'erreur d'arbitrage pour un numéro fournisseur déjà pris :
   * qui est déjà en place, et ce que j'essayais d'enregistrer.
   *
   * Si le document en place est introuvable (course improbable), on retombe sur
   * l'ancienne erreur : le triage la traitera comme un échec ordinaire plutôt
   * que de mentir avec un conflit vide.
   */
  private async describeNumberClash(
    input: CreatePurchaseDocumentInput,
  ): Promise<Error> {
    const { data } = await (this.supabase as any)
      .from('purchase_documents')
      .select('id, doc_type, doc_date, total_ttc, is_invoiced')
      .eq('supplier_id', input.supplierId)
      // Le type fait partie de la clé depuis 20260806140000 : un bon de
      // réception et sa facture partagent légitimement un numéro. Sans ce
      // filtre, `maybeSingle` tomberait sur deux lignes et rendrait la main
      // sans savoir laquelle décrire.
      .eq('doc_type', input.docType)
      .eq('number', input.number)
      .maybeSingle();
    if (!data) return new PurchaseNumberConflictError();

    const { data: supplier } = await (this.supabase as any)
      .from('authorized_suppliers')
      .select('name')
      .eq('id', input.supplierId)
      .maybeSingle();

    return new PurchaseNumberTakenError({
      docType: input.docType,
      number: input.number,
      supplierId: input.supplierId,
      supplierName: (supplier?.['name'] as string) ?? null,
      mineDocDate: input.docDate,
      mineTotalTtc: input.totalTtc ?? 0,
      mineLineCount: input.lines.length,
      existing: {
        id: data['id'] as string,
        docType: (data['doc_type'] as string) ?? '',
        docDate: (data['doc_date'] as string) ?? '',
        totalTtc: Number(data['total_ttc']) || 0,
        isInvoiced: (data['is_invoiced'] as boolean) ?? false,
      },
    });
  }

  /** Voir SupabaseSalesRepository.assertParentsNotAlreadyInvoiced. */
  private async assertParentsNotAlreadyInvoiced(
    input: CreatePurchaseDocumentInput,
  ): Promise<void> {
    if (input.docType !== 'invoice' || input.allowDuplicateParentInvoice)
      return;
    const parentIds = [
      ...new Set(
        [
          ...(input.parentDocIds ?? []),
          ...(input.parentDocId ? [input.parentDocId] : []),
        ].filter(Boolean),
      ),
    ];
    if (parentIds.length === 0) return;

    const candidates = await this.findLiveInvoicesForParents(parentIds);
    const clashes = findParentInvoicingClashes({
      parentIds,
      existingInvoices: candidates,
      // Les achats n'ont pas de clé d'idempotence persistée. Le seul numéro ne
      // suffit pas à reconnaître notre facture (deux fournisseurs peuvent
      // employer le même) : le rejeu exact est vérifié séparément, en incluant
      // fournisseur, parents, en-tête, totaux et lignes.
    });
    if (clashes.length === 0) return;

    const involved = clashingInvoices(clashes);
    const numbersById = await this.numbersForDocumentIds([
      ...parentIds,
      ...involved.flatMap((i) => i.parentDocIds),
    ]);
    throw new ParentAlreadyInvoicedError({
      scope: 'purchases',
      docType: input.docType,
      ownNumber: input.number,
      ownDocDate: input.docDate,
      ownTotalTtc: input.totalTtc ?? 0,
      ownParentNumbers: parentIds.map((id) => numbersById.get(id) ?? id),
      takenParents: clashes.map((c) => ({
        parentId: c.parentId,
        parentNumber: numbersById.get(c.parentId) ?? c.parentId,
        invoiceId: c.invoice.id,
      })),
      invoices: involved.map<ConflictingInvoiceSummary>((i) => ({
        id: i.id,
        number: i.number,
        docDate: i.docDate,
        totalTtc: i.totalTtc,
        parentDocIds: i.parentDocIds,
        parentNumbers: i.parentDocIds.map((pid) => numbersById.get(pid) ?? pid),
      })),
    });
  }

  /** Factures d'achat citant un des parents donnés, annulées exclues. */
  private async findLiveInvoicesForParents(parentIds: string[]) {
    const rows: Array<Record<string, unknown>> = [];
    for (let i = 0; i < parentIds.length; i += IN_FILTER_CHUNK_SIZE) {
      const chunk = parentIds.slice(i, i + IN_FILTER_CHUNK_SIZE);
      const { data, error } = await (this.supabase as any)
        .from('purchase_documents')
        .select(
          'id, number, doc_date, total_ttc, parent_doc_id, parent_doc_ids',
        )
        .eq('doc_type', 'invoice')
        .or(
          `parent_doc_id.in.(${chunk.join(',')}),parent_doc_ids.ov.{${chunk.join(',')}}`,
        );
      if (error) throw error;
      rows.push(...((data ?? []) as Array<Record<string, unknown>>));
    }
    const byId = new Map<string, Record<string, unknown>>();
    for (const row of rows) byId.set(row['id'] as string, row);
    if (byId.size === 0) return [];

    const cancelled = await this.cancelledInvoiceIds([...byId.keys()]);
    return [...byId.values()]
      .filter((row) => !cancelled.has(row['id'] as string))
      .map((row) => ({
        id: row['id'] as string,
        number: (row['number'] as string) ?? '',
        docDate: (row['doc_date'] as string) ?? '',
        totalTtc: Number(row['total_ttc']) || 0,
        parentDocIds: [
          ...new Set(
            [
              ...((row['parent_doc_ids'] as string[] | null) ?? []),
              ...(row['parent_doc_id'] ? [row['parent_doc_id'] as string] : []),
            ].filter(Boolean),
          ),
        ],
        clientRequestId: null,
      }));
  }

  /** Factures d'achat intégralement contre-passées par un avoir. */
  private async cancelledInvoiceIds(
    invoiceIds: string[],
  ): Promise<Set<string>> {
    const cancelled = new Set<string>();
    if (invoiceIds.length === 0) return cancelled;

    const totals = new Map<string, number>();
    const credited = new Map<string, number>();
    for (let i = 0; i < invoiceIds.length; i += IN_FILTER_CHUNK_SIZE) {
      const chunk = invoiceIds.slice(i, i + IN_FILTER_CHUNK_SIZE);
      const { data, error } = await (this.supabase as any)
        .from('purchase_documents')
        .select('id, total_ttc')
        .in('id', chunk);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        totals.set(row['id'] as string, Number(row['total_ttc']) || 0);
      }
      const { data: notes, error: notesError } = await (this.supabase as any)
        .from('purchase_documents')
        .select('total_ttc, parent_doc_id, parent_doc_ids')
        .eq('doc_type', 'credit_note')
        .or(
          `parent_doc_id.in.(${chunk.join(',')}),parent_doc_ids.ov.{${chunk.join(',')}}`,
        );
      if (notesError) throw notesError;
      for (const row of (notes ?? []) as Array<Record<string, unknown>>) {
        const parents = new Set(
          [
            ...((row['parent_doc_ids'] as string[] | null) ?? []),
            ...(row['parent_doc_id'] ? [row['parent_doc_id'] as string] : []),
          ].filter(Boolean),
        );
        for (const pid of parents) {
          credited.set(
            pid,
            (credited.get(pid) ?? 0) + (Number(row['total_ttc']) || 0),
          );
        }
      }
    }

    for (const [id, invoiceTotal] of totals) {
      const creditedTotal = credited.get(id) ?? 0;
      if (invoiceTotal > 0 && creditedTotal >= invoiceTotal - 0.0005)
        cancelled.add(id);
    }
    return cancelled;
  }

  private async numbersForDocumentIds(
    ids: string[],
  ): Promise<Map<string, string>> {
    const unique = [...new Set(ids.filter(Boolean))];
    const out = new Map<string, string>();
    for (let i = 0; i < unique.length; i += IN_FILTER_CHUNK_SIZE) {
      const chunk = unique.slice(i, i + IN_FILTER_CHUNK_SIZE);
      const { data, error } = await (this.supabase as any)
        .from('purchase_documents')
        .select('id, number')
        .in('id', chunk);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        out.set(row['id'] as string, (row['number'] as string) ?? '');
      }
    }
    return out;
  }

  /**
   * Réservé à une décision utilisateur dans le dialogue de conflits. Les
   * factures peuvent suivre l'arbitrage dédié, mais un BR déjà facturé garde
   * son verrou légal : l'interface ne propose pas de l'évincer et la RPC le
   * refuse aussi. On ne sacrifie jamais la chaîne de traçabilité au doublon de
   * numéro fournisseur.
   *
   * Couvre tous les types d'achat, parce que l'arbitrage porte ici sur deux
   * causes : la double facturation d'un bon de réception ET le doublon de
   * numéro fournisseur (qui peut viser une commande ou un BR).
   */
  async deleteDocumentForConflict(id: string): Promise<void> {
    const { data: doc, error: fetchErr } = await (this.supabase as any)
      .from('purchase_documents')
      .select('id, doc_type')
      .eq('id', id)
      .maybeSingle();
    if (fetchErr) throw fetchErr;
    // Idempotent : un rejeu après une tentative aboutie ne doit pas échouer.
    if (!doc) return;

    // Le BR, son grand-livre et ses dépendances documentaires sont supprimés
    // par la même transaction que le chemin normal. On le fait AVANT tout
    // nettoyage applicatif : un BR facturé/interdit ne doit jamais perdre ses
    // imputations à cause d'un arbitrage qui sera ensuite refusé.
    if (doc['doc_type'] === 'reception') {
      const { data, error } = await this.supabase.rpc(
        'delete_purchase_reception_atomic',
        { p_document_id: id },
      );
      if (error) throw error;
      const affectedArticleIds = (data ?? []) as unknown as string[];
      if (affectedArticleIds.length > 0) {
        await mirrorStorefrontStockForArticles(
          this.supabase,
          affectedArticleIds,
        );
      }
      return;
    }

    if (doc['doc_type'] === 'invoice') {
      // Seule une facture dupliquée encore inutilisée peut être évincée. La RPC
      // refuse toute allocation ou retenue liée AVANT de supprimer la facture,
      // ses lignes et de recalculer ses parents. Aucun paiement n'est déplacé :
      // réattribuer automatiquement de l'argent changerait l'intention saisie.
      const { error } = await this.supabase.rpc(
        'delete_purchase_invoice_for_conflict',
        { p_document_id: id },
      );
      if (error) throw error;
      return;
    }

    // Décaissements imputés : l'imputation part, le paiement reste (l'argent a
    // bien été versé) en crédit non affecté. On filtre sur le seul document_id :
    // `document_type` porte le type du document ('invoice', 'reception', …) et
    // ne distingue PAS vente et achat — l'id, lui, est unique. Ce qui reste ici
    // n'a trouvé sa place chez aucune source.
    const { error: paErr } = await this.supabase
      .from('payment_allocations')
      .delete()
      .eq('document_id', id);
    if (paErr) throw paErr;

    const { error: wcErr } = await (this.supabase as any)
      .from('withholding_certificates')
      .delete()
      .eq('certificate_type', 'purchase')
      .eq('source_document_id', id);
    if (wcErr) throw wcErr;

    const { error: delErr } = await this.supabase
      .from('purchase_documents')
      .delete()
      .eq('id', id);
    if (delErr) throw delErr;
  }

  /** Voir SupabaseSalesRepository.assertNotConcurrentlyEdited. */
  private async assertNotConcurrentlyEdited(
    id: string,
    input: UpdatePurchaseDocumentInput,
  ): Promise<void> {
    const base = input.baseUpdatedAt;
    if (!base) return;
    const { data, error } = await (this.supabase as any)
      .from('purchase_documents')
      .select('id, doc_type, number, updated_at, total_ttc')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    if (!data) return;
    const serverUpdatedAt = (data['updated_at'] as string) ?? '';
    if (!isConcurrentlyEdited(base, serverUpdatedAt)) return;

    const { count } = await (this.supabase as any)
      .from('purchase_document_lines')
      .select('id', { count: 'exact', head: true })
      .eq('document_id', id);
    throw new ConcurrentEditError({
      scope: 'purchases',
      documentId: id,
      docType: (data['doc_type'] as string) ?? '',
      number: (data['number'] as string) ?? '',
      baseUpdatedAt: base,
      serverUpdatedAt,
      mineTotalTtc: input.totalTtc ?? null,
      serverTotalTtc: Number(data['total_ttc']) || 0,
      mineLineCount: input.lines?.length ?? null,
      serverLineCount: count ?? 0,
    });
  }

  async updateDocument(
    id: string,
    input: UpdatePurchaseDocumentInput,
  ): Promise<PurchaseDocument> {
    await this.ensureVariantLineSchema(input.lines);
    if (input.clientRequestId) {
      const replay = await resumePurchaseDraftMutation(this.supabase, id, input);
      if (replay) {
        const saved = await this.getDocumentById(replay);
        if (!saved) throw new Error('purchase_document_not_found');
        return saved;
      }
    }

    const current = await this.getDocumentById(id);
    if (!current) throw new Error('purchase_document_not_found');

    if (input.clientRequestId) {
      const full: CreatePurchaseDocumentInput = {
        docType: current.docType, number: input.number ?? current.number,
        supplierId: input.supplierId ?? current.supplierId,
        warehouseId: input.warehouseId ?? current.warehouseId ?? undefined,
        clientRequestId: input.clientRequestId, docDate: input.docDate ?? current.docDate,
        // Existing source relationships are immutable during draft validation.
        parentDocId: current.parentDocId ?? undefined, parentDocIds: current.parentDocIds,
        notes: input.notes !== undefined ? input.notes : current.notes ?? undefined,
        customProperties: input.customProperties ?? current.customProperties,
        vatExempt: input.vatExempt ?? current.vatExempt,
        totalHt: input.totalHt ?? current.totalHt, totalVat: input.totalVat ?? current.totalVat,
        totalTtc: input.totalTtc ?? current.totalTtc, withholdingAmount: input.withholdingAmount ?? current.withholdingAmount,
        stampAmount: input.stampAmount ?? current.stampAmount, netToPay: input.netToPay ?? current.netToPay,
        lines: input.lines ?? (current.lines ?? []).map(line => ({
          articleId: line.articleId ?? undefined,
          variantId: line.variantId ?? null,
          variantLabel: line.variantLabel ?? null, description: line.description, quantity: line.quantity,
          unit: line.articleUnit, unitPriceHt: line.unitPriceHt, discountPct: line.discountPct,
          vatRate: line.vatRate, totalHt: line.totalHt, totalTtc: line.totalTtc, sourceDocId: line.sourceDocId,
        })),
      };
      if (current.docType === 'reception') {
        full.lines = await this.resolveReceptionLineUnits(full.lines, current.lines ?? []);
      }
      const savedId = await savePurchaseDraftMutation(this.supabase, id, input, full);
      const saved = await this.getDocumentById(savedId);
      if (!saved) throw new Error('purchase_document_not_found');
      if (current.docType === 'reception') {
        // The stock ledger and validation receipt are committed. A mirror failure cannot undo them.
        void mirrorStorefrontStockForArticles(this.supabase, [...new Set([
          ...(current.lines ?? []).map(line => line.articleId), ...full.lines.map(line => line.articleId),
        ].filter((articleId): articleId is string => Boolean(articleId)))]).catch(() => undefined);
      }
      return saved;
    }

    const receptionPostingMode =
      current.stockPostingMode ??
      (current.warehouseId ? 'live' : 'legacy_untracked');
    if (current.docType === 'reception' && receptionPostingMode !== 'live') {
      // A stockless historical snapshot is already represented by an opening
      // balance; changing its lines would rewrite history without changing that
      // balance. A legacy_untracked receipt has no provable warehouse and must
      // first pass through the explicit reconciliation flow. Neither may fall
      // through to ordinary two-step header/line DML (which RLS also blocks).
      throw new Error(
        receptionPostingMode === 'historical_snapshot'
          ? 'purchase_reception_historical_snapshot_read_only'
          : 'purchase_reception_legacy_reconciliation_required',
      );
    }
    if (current.docType === 'reception' && receptionPostingMode === 'live') {
      const merged: CreatePurchaseDocumentInput = {
        docType: 'reception',
        number: input.number ?? current.number,
        supplierId: input.supplierId ?? current.supplierId,
        warehouseId: input.warehouseId ?? current.warehouseId ?? undefined,
        clientRequestId:
          input.clientRequestId ?? current.clientRequestId ?? undefined,
        docDate: input.docDate ?? current.docDate,
        parentDocId: input.parentDocId ?? current.parentDocId ?? undefined,
        parentDocIds: input.parentDocIds ?? current.parentDocIds,
        notes: input.notes ?? current.notes ?? undefined,
        customProperties: input.customProperties ?? current.customProperties,
        vatExempt: input.vatExempt ?? current.vatExempt,
        totalHt: input.totalHt ?? current.totalHt,
        totalVat: input.totalVat ?? current.totalVat,
        totalTtc: input.totalTtc ?? current.totalTtc,
        withholdingAmount: input.withholdingAmount ?? current.withholdingAmount,
        stampAmount: input.stampAmount ?? current.stampAmount,
        netToPay: input.netToPay ?? current.netToPay,
        lines:
          input.lines ??
          (current.lines ?? []).map((line) => ({
            articleId: line.articleId ?? undefined,
            variantId: line.variantId ?? null,
            variantLabel: line.variantLabel ?? null,
            description: line.description,
            quantity: line.quantity,
            unit: line.articleUnit,
            unitPriceHt: line.unitPriceHt,
            discountPct: line.discountPct,
            vatRate: line.vatRate,
            totalHt: line.totalHt,
            totalTtc: line.totalTtc,
            sourceDocId: line.sourceDocId,
          })),
      };
      merged.lines = await this.resolveReceptionLineUnits(
        merged.lines,
        current.lines ?? [],
      );
      try {
        return await this.savePurchaseReceptionAtomic(
          id,
          merged,
          true,
          input.baseUpdatedAt,
        );
      } catch (error) {
        const databaseError = error as { code?: string; message?: string };
        if (
          input.baseUpdatedAt &&
          (databaseError.code === '40001' ||
            databaseError.message?.includes('concurrent_edit'))
        ) {
          // Reconstruit l'erreur métier détaillée utilisée par le dialogue de
          // synchronisation. L'écriture atomique a déjà été annulée côté DB.
          await this.assertNotConcurrentlyEdited(id, input);
        }
        throw error;
      }
    }

    if (current.docType === 'invoice') {
      // La relation source est immuable sur une édition : la RPC ne reçoit
      // volontairement aucun parent et conserve ceux du document verrouillé.
      // Construire ici l'état COMPLET permet aussi de reconnaître exactement
      // un rejeu après une réponse réseau perdue, sans accepter un conflit qui
      // ne partagerait que le numéro.
      const requested: CreatePurchaseDocumentInput = {
        docType: 'invoice',
        number: input.number ?? current.number,
        supplierId: input.supplierId ?? current.supplierId,
        docDate: input.docDate ?? current.docDate,
        parentDocId: current.parentDocId ?? undefined,
        parentDocIds: current.parentDocIds,
        notes:
          input.notes !== undefined
            ? input.notes
            : (current.notes ?? undefined),
        customProperties: input.customProperties ?? current.customProperties,
        vatExempt: input.vatExempt ?? current.vatExempt,
        totalHt: input.totalHt ?? current.totalHt,
        totalVat: input.totalVat ?? current.totalVat,
        totalTtc: input.totalTtc ?? current.totalTtc,
        withholdingAmount: input.withholdingAmount ?? current.withholdingAmount,
        stampAmount: input.stampAmount ?? current.stampAmount,
        netToPay: input.netToPay ?? current.netToPay,
        lines: input.lines
          ? input.lines.map((line) => ({ ...line, unit: line.unit ?? null }))
          : (current.lines ?? []).map((line) => ({
              articleId: line.articleId ?? undefined,
              variantId: line.variantId ?? null,
              variantLabel: line.variantLabel ?? null,
              description: line.description,
              quantity: line.quantity,
              unit: line.articleUnit ?? null,
              unitPriceHt: line.unitPriceHt,
              discountPct: line.discountPct,
              vatRate: line.vatRate,
              totalHt: line.totalHt,
              totalTtc: line.totalTtc,
              sourceDocId: line.sourceDocId,
            })),
      };
      const lines = requested.lines.map((line, index) => ({
        line_number: index + 1,
        article_id: line.articleId ?? null,
        variant_id: line.variantId ?? null,
        variant_label: line.variantLabel ?? null,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit ?? null,
        unit_price_ht: line.unitPriceHt,
        purchase_price: line.unitPriceHt,
        discount_pct: line.discountPct ?? 0,
        vat_rate: line.vatRate,
        total_ht: line.totalHt ?? 0,
        total_ttc: line.totalTtc ?? 0,
        source_doc_id: line.sourceDocId ?? null,
      }));

      const { data, error } = await this.supabase.rpc(
        'update_purchase_invoice_atomic',
        {
          p_document_id: id,
          p_number: requested.number,
          p_supplier_id: requested.supplierId,
          p_doc_date: requested.docDate,
          p_notes: requested.notes ?? null,
          p_custom_properties: requested.customProperties ?? {},
          p_vat_exempt: requested.vatExempt ?? false,
          p_total_ht: requested.totalHt ?? 0,
          p_total_vat: requested.totalVat ?? 0,
          p_total_ttc: requested.totalTtc ?? 0,
          p_withholding_amount: requested.withholdingAmount ?? 0,
          p_stamp_amount: requested.stampAmount ?? 0,
          p_net_to_pay: requested.netToPay ?? 0,
          p_lines: lines,
          p_base_updated_at: input.baseUpdatedAt ?? null,
        },
      );

      if (error) {
        if (isUniqueViolation(error, SUPPLIER_NUMBER_INDEXES)) {
          throw new PurchaseNumberConflictError();
        }
        const databaseError = error as { code?: string; message?: string };
        if (
          input.baseUpdatedAt &&
          (databaseError.code === '40001' ||
            databaseError.message?.includes('concurrent_edit'))
        ) {
          const replay = await this.getDocumentById(id);
          if (replay && isExactPurchaseInvoiceReplay(replay, requested)) {
            return replay;
          }
          // La transaction a déjà annulé la modification. Reconstruire ici le
          // même conflit détaillé que les autres éditions permet au dialogue de
          // synchronisation d'afficher les versions mine/serveur.
          await this.assertNotConcurrentlyEdited(id, input);
        }
        throw error;
      }

      const result = data as unknown as { document_id?: string } | null;
      const saved = await this.getDocumentById(result?.document_id ?? id);
      if (!saved) throw new Error('purchase_invoice_not_updated');
      // Ne jamais relancer le report de règlements lors d'une simple édition :
      // il appartient uniquement à la transformation initiale.
      return saved;
    }

    await this.assertNotConcurrentlyEdited(id, input);
    const mapped: TablesUpdate<'purchase_documents'> = {};
    if (input.number !== undefined) mapped['number'] = input.number;
    if (input.supplierId !== undefined)
      mapped['supplier_id'] = input.supplierId;
    if (input.docDate !== undefined) mapped['doc_date'] = input.docDate;
    if (input.notes !== undefined) mapped['notes'] = input.notes;
    if (input.customProperties !== undefined)
      (mapped as Record<string, unknown>)['custom_properties'] =
        input.customProperties;
    if (input.vatExempt !== undefined)
      (mapped as Record<string, unknown>)['vat_exempt'] = input.vatExempt;
    if (input.totalHt !== undefined) mapped['total_ht'] = input.totalHt;
    if (input.totalVat !== undefined) mapped['total_vat'] = input.totalVat;
    if (input.totalTtc !== undefined) mapped['total_ttc'] = input.totalTtc;
    if (input.withholdingAmount !== undefined)
      mapped['withholding_amount'] = input.withholdingAmount;
    if (input.stampAmount !== undefined)
      mapped['stamp_amount'] = input.stampAmount;
    if (input.netToPay !== undefined) mapped['net_to_pay'] = input.netToPay;

    // Voir le repo ventes : sans ça, une modification qui ne touche que les
    // lignes laisse `updated_at` figé et devient invisible à la détection de
    // modification concurrente.
    if (Object.keys(mapped).length === 0 && input.lines) {
      (mapped as Record<string, unknown>)['updated_at'] =
        new Date().toISOString();
    }

    if (Object.keys(mapped).length > 0) {
      const { error } = await this.supabase
        .from('purchase_documents')
        .update(mapped)
        .eq('id', id);
      if (error) {
        if (isUniqueViolation(error, SUPPLIER_NUMBER_INDEXES)) {
          throw new PurchaseNumberConflictError();
        }
        throw error;
      }
    }

    // Replace lines if provided
    if (input.lines) {
      // Delete old lines
      const { error: delError } = await this.supabase
        .from('purchase_document_lines')
        .delete()
        .eq('document_id', id);
      if (delError) throw delError;

      // Insert new lines
      if (input.lines.length > 0) {
        const lines = input.lines.map((line, idx) => ({
          document_id: id,
          line_number: idx + 1,
          article_id: line.articleId,
          variant_id: line.variantId ?? null,
          variant_label: line.variantLabel ?? null,
          description: line.description,
          quantity: line.quantity,
          unit: line.unit ?? null,
          unit_price_ht: line.unitPriceHt,
          purchase_price: line.unitPriceHt ?? 0,
          discount_pct: line.discountPct ?? 0,
          vat_rate: line.vatRate,
          total_ht: line.totalHt ?? 0,
          total_ttc: line.totalTtc ?? 0,
          // Sans ce report, la première réécriture des lignes effacerait
          // l'origine BR et la facture se relirait à plat.
          source_doc_id: line.sourceDocId ?? null,
        }));
        const { error: insError } = await this.supabase
          .from('purchase_document_lines')
          .insert(lines);
        if (insError) throw insError;
      }
    }

    return (await this.getDocumentById(id))!;
  }

  async deleteDocument(id: string): Promise<void> {
    const { data: doc, error: fetchErr } = await this.supabase
      .from('purchase_documents')
      .select('id, doc_type, is_invoiced, parent_doc_id, parent_doc_ids')
      .eq('id', id)
      .single();
    if (fetchErr) throw fetchErr;

    // Les factures d'achat sont des documents numérotés/légaux : interdiction de
    // suppression définitive (seul un avoir peut les annuler).
    if (doc?.['doc_type'] === 'invoice') {
      throw new Error('PURCHASE_INVOICE_DELETE_FORBIDDEN');
    }
    // Une commande/un BR déjà facturé ne peut pas être supprimé non plus :
    // il est référencé par une facture existante.
    if (doc?.['is_invoiced'] === true) {
      throw new Error('PURCHASE_INVOICED_DELETE_FORBIDDEN');
    }

    // Seuls les documents non facturés et non-factures arrivent ici (commande, BR,
    // avoir). Aucun ré-ajustement de is_invoiced parent n'est nécessaire puisque les
    // factures — seules à marquer leurs parents — ne sont jamais supprimables.

    // Le BR et ses mouvements disparaissent dans la même transaction : aucun
    // état intermédiaire ne peut laisser le stock gonflé ou retirer le stock en
    // conservant le document.
    if (doc?.['doc_type'] === 'reception') {
      const { data, error } = await this.supabase.rpc(
        'delete_purchase_reception_atomic',
        { p_document_id: id },
      );
      if (error) throw error;
      const affectedArticleIds = (data ?? []) as unknown as string[];
      if (affectedArticleIds.length > 0) {
        await mirrorStorefrontStockForArticles(
          this.supabase,
          affectedArticleIds,
        );
      }
      return;
    }

    const { error: delErr } = await this.supabase
      .from('purchase_documents')
      .delete()
      .eq('id', id);
    if (delErr) throw delErr;
  }

  async markAsInvoiced(id: string): Promise<void> {
    // Le durcissement des BR interdit volontairement les UPDATE directs afin
    // que leurs lignes et leur stock restent atomiques. Cette mutation étroite
    // recalcule `is_invoiced` depuis les relations facture→parent réellement
    // persistées ; l'appelant ne peut donc ni forcer true ni forcer false.
    const { error } = await this.supabase.rpc(
      'mark_purchase_document_invoiced',
      { p_document_id: id },
    );
    if (error) throw error;
  }

  async getChildDocuments(
    parentId: string,
    allowedTypes?: readonly PurchaseDocType[],
  ): Promise<PurchaseDocument[]> {
    if (allowedTypes?.length === 0) return [];
    let query = (this.supabase as any)
      .from('purchase_documents')
      .select(
        '*, purchase_document_lines(*, article:authorized_articles(code, name, is_invoiceable, unit))',
      )
      .contains('parent_doc_ids', [parentId]);
    if (allowedTypes) query = query.in('doc_type', allowedTypes);
    const { data, error } = await query
      .order('created_at', { ascending: false })
      .order('doc_date', { ascending: false });
    if (error) throw error;
    return (data ?? []).map(mapPurchaseDocument);
  }
}

function applyPurchaseDocumentFilters(
  query: any,
  filters?: PurchaseDocumentFilters,
): any {
  // Delta du miroir hors-ligne — voir `updatedSince` dans les filtres.
  if (filters?.updatedSince)
    query = query.gt('updated_at', filters.updatedSince);
  if (filters?.docType) query = query.eq('doc_type', filters.docType);
  if (filters?.search?.trim())
    query = query.or(orContainsAnyColumn(['number'], filters.search.trim()));
  if (filters?.supplierIds?.length)
    query = query.in('supplier_id', filters.supplierIds);
  else if (filters?.supplierId)
    query = query.eq('supplier_id', filters.supplierId);
  if (filters?.dateFrom) query = query.gte('doc_date', filters.dateFrom);
  if (filters?.dateTo) query = query.lte('doc_date', filters.dateTo);
  if (filters?.isInvoiced !== undefined)
    query = query.eq('is_invoiced', filters.isInvoiced);
  // Cf. sales-repository : `parent_doc_ids` porte toujours la filiation.
  if (filters?.parentDocIdIn?.length) {
    query = query.overlaps('parent_doc_ids', filters.parentDocIdIn);
  }
  // Liste d'ids vide = aucun document — cf. sales-repository.
  if (filters?.ids) query = query.in('id', filters.ids);
  return query;
}

// UI column key → purchase_documents DB column, for server-side ordering.
const PURCHASE_SORT_COLUMNS: Record<string, string> = {
  number: 'number',
  docDate: 'doc_date',
  totalHt: 'total_ht',
  totalVat: 'total_vat',
  totalTtc: 'total_ttc',
  withholdingAmount: 'withholding_amount',
  stampAmount: 'stamp_amount',
  netToPay: 'net_to_pay',
  isInvoiced: 'is_invoiced',
  notes: 'notes',
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  // Hour-only display columns — they sort by the timestamp they render.
  createdTime: 'created_at',
  updatedTime: 'updated_at',
};

function applyPurchaseDocumentSort(
  query: any,
  sort?: { field: string; direction: 'asc' | 'desc' },
): any {
  const column = sort ? PURCHASE_SORT_COLUMNS[sort.field] : undefined;
  if (column) {
    query = query.order(column, { ascending: sort!.direction === 'asc' });
    if (column !== 'created_at')
      query = query.order('created_at', { ascending: false });
    // `created_at` is NOT unique (legacy imports share a transaction
    // timestamp) — see withStableOrder.
    return withStableOrder(query);
  }
  // Default: newest CREATED first, `doc_date` en retombée — voir
  // compareDocumentsNewestFirst pour les deux raisons.
  return withStableOrder(
    query
      .order('created_at', { ascending: false })
      .order('doc_date', { ascending: false }),
  );
}

export function mapPurchaseDocumentSummary(
  row: Record<string, unknown>,
): PurchaseDocumentSummary {
  return {
    id: row['id'] as string,
    number: (row['number'] as string) ?? '',
    docType: row['doc_type'] as PurchaseDocumentSummary['docType'],
    docDate: row['doc_date'] as string,
    supplierId: (row['supplier_id'] as string) ?? '',
    isInvoiced: (row['is_invoiced'] as boolean) ?? false,
    totalTtc: Number(row['total_ttc']) || 0,
    createdAt: (row['created_at'] as string) ?? '',
  };
}

function mapPurchaseDocument(row: Record<string, unknown>): PurchaseDocument {
  const lines =
    (row['purchase_document_lines'] as Record<string, unknown>[] | undefined) ??
    [];
  return {
    id: row['id'] as string,
    docType: row['doc_type'] as PurchaseDocument['docType'],
    number: row['number'] as string,
    supplierId: row['supplier_id'] as string,
    warehouseId: (row['warehouse_id'] as string) ?? null,
    stockPostingMode:
      (row['stock_posting_mode'] as PurchaseDocument['stockPostingMode']) ??
      (row['warehouse_id'] ? 'live' : 'legacy_untracked'),
    clientRequestId: (row['client_request_id'] as string) ?? null,
    docDate: row['doc_date'] as string,
    parentDocId: (row['parent_doc_id'] as string) ?? null,
    parentDocIds: (row['parent_doc_ids'] as string[]) ?? [],
    isInvoiced: (row['is_invoiced'] as boolean) ?? false,
    vatExempt: (row['vat_exempt'] as boolean) ?? false,
    totalHt: (row['total_ht'] as number) ?? 0,
    totalVat: (row['total_vat'] as number) ?? 0,
    totalTtc: (row['total_ttc'] as number) ?? 0,
    withholdingAmount: (row['withholding_amount'] as number) ?? 0,
    stampAmount: (row['stamp_amount'] as number) ?? 0,
    netToPay: (row['net_to_pay'] as number) ?? 0,
    notes: (row['notes'] as string) ?? null,
    customProperties:
      (row['custom_properties'] as PurchaseDocument['customProperties']) ?? {},
    createdBy: (row['created_by'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    // L'embed PostgREST n'a pas d'ORDER BY — voir sortByLineNumber.
    lines: sortByLineNumber(lines.map(mapPurchaseLine)),
  };
}

function mapPurchaseLine(row: Record<string, unknown>): PurchaseDocumentLine {
  const article = row['article'] as Record<string, unknown> | null | undefined;
  const unitSnapshot = row['unit'] as string | null | undefined;
  return {
    id: row['id'] as string,
    documentId: row['document_id'] as string,
    lineNumber: row['line_number'] as number,
    articleId: (row['article_id'] as string) ?? null,
    variantId: (row['variant_id'] as string) ?? null,
    variantLabel: (row['variant_label'] as string) ?? null,
    description: row['description'] as string,
    quantity: row['quantity'] as number,
    unitPriceHt: row['unit_price_ht'] as number,
    purchasePrice: (row['purchase_price'] as number) ?? 0,
    discountPct: (row['discount_pct'] as number) ?? 0,
    vatRate: row['vat_rate'] as number,
    totalHt: row['total_ht'] as number,
    totalTtc: row['total_ttc'] as number,
    sourceDocId: (row['source_doc_id'] as string) ?? null,
    articleCode: (article?.['code'] as string) ?? null,
    articleName: (article?.['name'] as string) ?? null,
    articleIsInvoiceable: article
      ? ((article['is_invoiceable'] as boolean | null) ?? true)
      : null,
    articleUnit: unitSnapshot ?? (article?.['unit'] as string | null) ?? null,
  };
}
